import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { AnalyticsCards } from "Common/Components/AnalyticsCards/AnalyticsCards";
import PrimarySubmitButton from "Common/Components/Buttons/PrimarySubmitButton";
import { defaultInnerStyle } from "Common/Components/PageContainer/PageContainer";
import { app_modules } from "helpers/apps_modules_list";
import usePermission from "hooks/usePermission";
import { ClipboardList, FileText } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { createSelector } from "reselect";
import { fetchPOAnalytics } from "slices/Procurement/purchase-order/purchase-order/thunk";
import ApprovedPR from "./ApprovedPR/ApprovedPR";
import PurchaseOrderList from "./PurchaseOrder/PurchaseOrder";
import { clearAllTableStates } from "utils/clearTableStates";

const PurchaseOrder = () => {
  const dispatch = useDispatch<any>();
  const [tab, setTab] = useState(
    Number(sessionStorage?.getItem("purchaseOrderTab")) || 1
  );
  const navigate = useNavigate();
  const location = useLocation();

  const { canCreate } = usePermission(
    app_modules?.purchase_management?.app_name,
    app_modules?.purchase_management?.modules?.purchase_order
  );

  useEffect(() => {
    const incomingTab = location.state?.tab;
    if (incomingTab) {
      setTab(incomingTab);
      clearAllTableStates();
      sessionStorage.setItem("purchaseOrderTab", incomingTab.toString());
    }
  }, [location.state]);

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      const newTab = newValue + 1;
      clearAllTableStates();
      setTab(newTab);
      sessionStorage.setItem("purchaseOrderTab", newTab.toString());
    },
    []
  );

  const selectPOs = createSelector(
    (state: any) => state.POreducer,
    (po) => ({
      analyticsData: po.analyticsData,
    })
  );

  const { analyticsData } = useSelector(selectPOs);

  useEffect(() => {
    dispatch(fetchPOAnalytics());
  }, [dispatch]);

  const statuses = useMemo(
    () => [
      {
        label: "Total Purchase Order",
        icon: <FileText size={16} />,
        bgColor: "bg-blue-50",
        textColor: "text-blue-600",
        count: analyticsData?.total_count ?? 0,
      },
      {
        label: "Pending PO Inward",
        icon: <ClipboardList size={16} />,
        bgColor: "bg-red-50",
        textColor: "text-red-600",
        count: analyticsData?.pending_count ?? 0,
      },
      {
        label: "Partially Inwarded",
        icon: <FileText size={16} />,
        bgColor: "bg-yellow-50",
        textColor: "text-yellow-600",
        count: analyticsData?.partially_inwarded_count ?? 0,
      },
      {
        label: "Complete Inwarded",
        icon: <FileText size={16} />,
        bgColor: "bg-green-50",
        textColor: "text-green-600",
        count: analyticsData?.complete_inwarded_count ?? 0,
      },
    ],
    [analyticsData]
  );

  const handleChildTabChange = useCallback((val: number) => {
    setTab(val);
    sessionStorage.setItem("purchaseOrderTab", val.toString());
  }, []);

  return (
    <div
      className="page-content px-3 mecord-kpi-list-page"
      style={{ "--mecord-table-offset": "380px" } as React.CSSProperties}
    >
      <div className="flex-shrink-0">
        <AnalyticsCards statuses={statuses} />
      </div>

      <div
        className="mecord-list-panel"
        style={{
          ...defaultInnerStyle,
          flex: "1 1 0",
          maxHeight: "100%",
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div className="flex-shrink-0">
          <div className="mecord-toolbar-title">
            <h6>Purchase Order</h6>
            {canCreate && (
              <PrimarySubmitButton
                label="Create Purchase Order"
                isLoading={false}
                loadingLabel="Submitting..."
                onClick={() => navigate("/create-direct-purchase-order")}
              />
            )}
          </div>

          <div className="mecord-tabs-wrap">
            <Box sx={{ width: "100%" }}>
              <Tabs
                value={tab - 1}
                onChange={handleTabChange}
                sx={{
                  minHeight: 30,
                  "& .MuiTab-root": {
                    fontFamily: "'Poppins', sans-serif",
                    textTransform: "none",
                    color: "#555555",
                    minHeight: 30,
                    paddingTop: "2px",
                    paddingBottom: "2px",
                    transition: "color 0.2s ease",
                  },
                  "& .MuiTab-root:hover": {
                    color: "#d2363a",
                  },
                  "& .Mui-selected": {
                    color: "#d2363a !important",
                  },
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#d2363a",
                    height: "3px",
                    transition: "all 0.3s ease",
                  },
                }}
              >
                <Tab label="Approved PR" />
                <Tab label="Purchase Order" />
              </Tabs>
              <Divider sx={{ mt: 0, borderColor: "#c35f61" }} />
            </Box>
          </div>
        </div>

        <div
          className="d-flex flex-column"
          style={{
            flex: "1 1 auto",
            minHeight: 0,
            maxHeight: "100%",
            overflow: "hidden",
          }}
        >
          {tab === 1 ? (
            <ApprovedPR
              handleTabChange={handleChildTabChange}
              noWrapper={true}
            />
          ) : tab === 2 ? (
            <PurchaseOrderList
              handleTabChange={handleChildTabChange}
              noWrapper={true}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default PurchaseOrder;
